import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Newspaper } from "lucide-react";
import { apiFetch } from "../lib/apiFetch";
import { getNewsImageUrl } from "../lib/newsUtils";

export function NewsTicker() {
  const { t } = useTranslation();
  const [news, setNews] = useState<any[]>(() => {
    try {
      const cached = localStorage.getItem("marketNewsCache");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Date.now() - (parsed.timestamp || 0) < 5 * 60 * 1000 && parsed.news?.length > 0) {
          return parsed.news;
        }
      }
    } catch (e) { }
    return [];
  });
  const [loading, setLoading] = useState(() => news.length === 0);
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});

  const handleImageError = (id: string) => {
    setFailedImages((prev) => ({ ...prev, [id]: true }));
  };

  useEffect(() => {
    let active = true;
    async function fetchNews() {
      try {
        const cacheKey = "marketNewsCache";
        const cached = localStorage.getItem(cacheKey);
        const now = Date.now();
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (now - parsed.timestamp < 5 * 60 * 1000 && parsed.news?.length > 0) {
              if (active) {
                setNews(parsed.news);
                setLoading(false);
              }
              return;
            }
          } catch (e) { }
        }

        const res = await apiFetch("/api/market-news");
        const data = await res.json();
        if (active && Array.isArray(data) && data.length > 0) {
          setNews(data);
          localStorage.setItem(cacheKey, JSON.stringify({ news: data, timestamp: now }));
        }
      } catch (err) {
        console.error("Failed to fetch market news", err);
      } finally {
        if (active) setLoading(false);
      }
    }
    fetchNews();

    return () => { active = false; };
  }, []);

  if (loading && news.length === 0) {
    return (
      <div className="ticker-container relative w-full h-[38px] bg-zinc-900/30 border border-zinc-800/30 rounded-sm animate-pulse"></div>
    );
  }

  if (news.length === 0) {
    return null;
  }

  // Double the array for seamless scrolling
  const duplicatedNews = [...news, ...news];

  return (
    <div className="ticker-container relative w-full h-[38px] overflow-hidden bg-zinc-900/50 border border-zinc-800/50 rounded-sm flex items-center">
      <div className="hidden sm:flex absolute top-0 left-0 h-full bg-zinc-900/90 z-10 items-center px-4 border-r border-zinc-800/80 shadow-[10px_0_15px_-3px_rgba(0,0,0,0.5)]">
        <Newspaper size={14} className="text-emerald-500 mr-2 shrink-0" />
        <span className="text-[10px] uppercase font-bold tracking-widest text-zinc-300 whitespace-nowrap">
          {t("header.breaking_news") || "Live Market Updates"}
        </span>
      </div>

      <div className="flex overflow-hidden relative w-full items-center">
        <style>
          {`
            @keyframes infinite-scroll {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .animate-infinite-scroll {
              animation: infinite-scroll 100s linear infinite;
            }
            .ticker-container:hover .animate-infinite-scroll {
              animation-play-state: paused;
            }
          `}
        </style>

        {/* We add padding to offset the fixed label on the left (only on sm+) */}
        <div className="flex whitespace-nowrap animate-infinite-scroll pl-4 sm:pl-[240px] py-1.5 items-center">
          {duplicatedNews.map((item, index) => {
            const itemId = `${item.uuid || item.link}-${index}`;
            const timeAgo = item.providerPublishTime
              ? formatTimeAgo(item.providerPublishTime)
              : "";
            const imgUrl = getNewsImageUrl(item, 'small');
            const hasImg = Boolean(imgUrl) && !failedImages[itemId];

            return (
              <a
                key={itemId}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center mx-5 group py-0.5"
              >
                {hasImg ? (
                  <img
                    src={imgUrl!}
                    alt={item.publisher || item.title}
                    onError={() => handleImageError(itemId)}
                    className="w-5 h-5 rounded object-cover mr-2 shrink-0 border border-zinc-700/60 group-hover:border-emerald-400 transition-colors"
                  />
                ) : (
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-2.5 shrink-0 group-hover:scale-125 transition-transform"></span>
                )}
                <span className="text-zinc-300 text-xs font-medium group-hover:text-emerald-400 transition-colors">
                  {item.title}
                </span>
                {(item.publisher || timeAgo) && (
                  <span className="text-zinc-400 text-[10px] ml-2 font-mono">
                    [{[item.publisher, timeAgo].filter(Boolean).join(" • ")}]
                  </span>
                )}
              </a>
            );
          })}
        </div>
      </div>

      {/* Right gradient mask for smooth fade out */}
      <div className="absolute top-0 right-0 h-full w-12 bg-gradient-to-l from-zinc-900/80 to-transparent z-10 pointer-events-none"></div>
    </div>
  );
}

function formatTimeAgo(dateInput: any) {
  if (!dateInput) return "";
  const date = typeof dateInput === "number" && dateInput < 3e10
    ? new Date(dateInput * 1000)
    : new Date(dateInput);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return `${Math.max(0, diffInSeconds)}s ago`;
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d ago`;
}
